import { ActivatedRoute, Params, Router } from '@angular/router';
import { BaseComponent } from '../../base/base.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { fuseAnimations } from '@fuse/animations';
import { take } from 'rxjs/operators';
import { IEntityDashboardData } from 'app/shared/interfaces/entityDashboard';
import { MatDialog } from '@angular/material/dialog';
import { ReferralDialogComponent } from '../referral-dialog/referral-dialog.component';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { EventService } from 'app/shared/services/event.service';
import { SubjectType } from 'app/shared/models/subject-type';
import { SubjectSettingService } from 'app/shared/services/subject-setting.service';
import { enumReviewStatus } from 'app/shared/models/review';
import { SubjectService } from 'app/shared/services/subject.service';
import { User } from 'app/shared/models/user';
import { SubjectNote } from 'app/shared/models/subject-note';
import { Note } from 'app/shared/models/note';
import { SubjectStopwatchService } from 'app/shared/services/subject-stopwatch/subject-stopwatch.service';
import { DateHelper } from '../../misc/date.module';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { NoteService } from 'app/shared/services/note.service';

@AutoUnsubscribe()
@Component({
   selector: 'referral-subject-dashboard',
   template: `<entity-dashboard
               class='h-100-p'
               [data]=data
               (onAdd) = add()
               (onGoBack) = goBack($event)
               (onRefreshData) = refreshData($event)
               fxFill
               ></entity-dashboard>`,
   animations: fuseAnimations,
 })
 export class ReferralSubjectDashboardComponent extends BaseComponent implements OnInit, OnDestroy {

   data: IEntityDashboardData;
   routeParams: Params;
   autoGenerateReference = false;

   onParamsChanged: Subscription;
   onSubjectChanged: Subscription;
   onAcceptSubject: Subscription;
   onRejectSubject: Subscription;
   
   constructor(
      private activeRoute: ActivatedRoute,
      private router: Router,
      private defaultDataService: DefaultDataService,
      private subjectTypeService: SubjectTypeService,
      private subjectService: SubjectService,
      private subjectSettingService: SubjectSettingService,
      private eventService: EventService,      
      private noteService: NoteService,
      private subjectStopwatchService: SubjectStopwatchService,
      private dateHelper: DateHelper,
      public dialog: MatDialog){
      super();
   }
   
   ngOnInit()
   {
      this.onParamsChanged = this.activeRoute.params.subscribe((routeParams) => {

         this.subjectStopwatchService.start(routeParams.id);

         this.routeParams = routeParams;
         const uid = routeParams.id;
         if (uid === 'new'){

            this.subjectTypeService.getById(this.defaultDataService.referralPrimarySubjectTypeId).pipe(take(1)).subscribe((subjectType) => {
               
               this.data = {
                  entity: undefined,
                  service: this.subjectService,
                  subjectType,
                  primary: true,
                  isReferral: true,
               };
               
               // new referral...
               this.add();
            });
         }
         else{
            if (uid && ((!this.data) || (!this.data.entity) || (this.data && this.data.entity && (uid !== this.data.entity.uid)))){
                  
               // dummy, to help with initial display
               // if (!(this.data && this.data.subjectType)){
               //    this.data = {
               //       entity: undefined,
               //       service: this.subjectService,
               //       subjectType,
               //       primary: true,
               //    };
               // }

               // get real data
               this.refreshData(uid);
            }
         }
      });
      
      // Subscribe to update subject on change to subject to be displayed
      this.onSubjectChanged = this.subjectService.onItemChanged.subscribe((subject) => {
         // if the current subject is changed, refresh...
         if ((this.data && this.data.entity) && (subject && (subject.uid === this.routeParams.id))){
            
            // dummy, to help with initial display
            if (!this.data.subjectType){
               this.data = {
                  entity: undefined,
                  service: this.subjectService,
                  subjectType: subject.subjectType,
                  primary: subject.isPrimary,
                  isReferral: true,
               };
            }

            // get real data
            this.refreshData(subject.uid);
         }
      });

      this.onAcceptSubject = this.eventService.onAcceptSubject.subscribe((subject) => {
        console.log('An accept review has been added to referral', subject);
        
        if ((this.data && this.data.entity) && (subject && (subject.uid === this.routeParams.id))){
          
          console.log('Referral confirmed as ok to process');
          
          this.subjectService.onAfterSave.pipe(take(1)).subscribe((savedSubject) => {
            
            console.log('Referral successfully converted to Subject', savedSubject);
            
            try{
              // add note to subject
              console.log('Add review/referral note...', savedSubject);
              this.AddReviewAndReferralAsNote(savedSubject);
            }
            catch(e){
              if (e instanceof Error){
                console.log('Failed to add review/referral note when converting subject', e.message);
              }
              // do nothing - if note not added we still want to continue
            }

            // navigate to subject dashboard
            console.log('Referral>Subject completed, re-routing to subject');
            this.router.navigateByUrl(`/subjects/${savedSubject.subjectType.name}/${savedSubject.uid}`);
          });
          setTimeout(() => {
            subject.isReferral = false;
            this.subjectService.clearCache();
            this.subjectService.save(subject);
          }, 100);
        }
      });

      this.onRejectSubject = this.eventService.onRejectSubject.subscribe((subject) => {
         if ((this.data && this.data.entity) && (subject && (subject.uid === this.routeParams.id))){
            // if rejected then return to the referrals list...
            this.router.navigateByUrl(`/referrals/referrals/all`);
         }
      });
   }

   ngOnDestroy(){
      this.onParamsChanged?.unsubscribe();
      this.onSubjectChanged?.unsubscribe();
      this.onAcceptSubject?.unsubscribe();
      this.onRejectSubject?.unsubscribe();
   }

   // add new referral
   add(){
      this.dialog.open(ReferralDialogComponent, {
         data : {},
         autoFocus: false,
      }).afterClosed().subscribe((result) => {
         if (result && result.subject){
            this.router.navigateByUrl(`/referrals/referrals/subject/${result.subject.uid}`);
         }
         else{
            this.router.navigateByUrl(`/referrals/referrals/all`);
         }
      });
   }

   refreshData(uid){
      if (uid){
         this.subjectService.getFullSubject(uid).pipe(take(1)).subscribe((subject) => {  
            if (subject){   
 
               // return data
               this.data = {
                  entity: subject,
                  service: this.subjectService,
                  subjectType: subject.subjectType,
                  primary: ((subject.referral && subject.referral.id) ? true : false),
                  isReferral: true,
               };

               this.getSubjectSettings(subject.subjectType);
            }
         });
      }
      else{
         this.router.navigateByUrl(`/referrals/referrals/all`);
      }
   }

   getSubjectSettings(subjectType: SubjectType){
      // get the subject settings so we can check for autogenerate when building the form
      this.subjectSettingService.getBySubjectTypeId(subjectType.id).subscribe(
         (setting) => { this.autoGenerateReference = setting && setting.autoGenerateReference; },
       );
   }

   goBack(lastSubject){
      // navigate to previous url
      this.router.navigateByUrl(`/referrals/referrals/subject/${lastSubject.key}`);
   }

   // guard function called if the user attemps to navigate away from this route   

   canDeactivate(route, state, nextState): Observable<boolean> | boolean {
      // check route - this handles any further action in relation to subject stopwatch
      // the returned value from this call will always be true as once we have handlded stopwatch
      // we don't want to prevent users from navigating away from dashboard
      return this.subjectStopwatchService.checkTime(nextState.url);

      // return true;
   }

   private AddReviewAndReferralAsNote(subject) {
      // add referral and review details to notes if copying from referral subject to subject
      if (subject && subject.hasReferral) {
        const subjectReview = subject.subjectReviews.find((i) => i.review && i.review.status && (i.review.status === enumReviewStatus.Accepted));
        if (subjectReview) {
          const review = subjectReview.review;
          const reviewedBy = new User(review.reviewedBy);
          const referral = subject.referral;
          const reviewComments = (review.comments && (review.comments.length > 0)) ? `Review comments: ${review.comments}` : `There were no review comments.`;
          const noteContent = `Added from referral submitted on ${this.dateHelper.getDateAsString(referral.referralDate)} ` +
            `by ${referral.referrerName} and accepted by ${reviewedBy.fullName} ` +
            `on ${this.dateHelper.getDateAsString(review.reviewDate)}.  ${reviewComments}`;
          const subjectNote = new SubjectNote(subject.id, new Note({ detail: noteContent, user: review.reviewedBy }));
          subjectNote.sequenceId = -1;  // MRD - a value of < 0 will force a sequenceId to be assigned and thus lock the note - see note service

          // save the note - I have added a 3 second delay to give the routing a chance to re-route and display subject dashboard
          setTimeout(() =>{
            this.noteService.saveNote(subjectNote,subject,false);
          }, 3000);
        }
      }
    }
}
